import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import './FareSplitPage.css';
import { calcGas, computeResult, encodeState, decodeState } from '../utils/fareSplitCalc';

// ── ユーティリティ ──────────────────────────────────────────────
function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

function fmt(n) {
  return Math.round(n).toLocaleString('ja-JP');
}

function initials(name) {
  return (name || '').slice(0, 1).toUpperCase() || '？';
}

function newExpense(name = '') {
  return { id: uid(), name, amount: '', paidBy: '', targets: 'all', targetIds: [] };
}

function makeDefault() {
  const p1 = { id: uid(), name: '田中', isDriver: true };
  const p2 = { id: uid(), name: '佐藤', isDriver: false };
  const p3 = { id: uid(), name: '鈴木', isDriver: false };
  return {
    tripName: '',
    driverRate: 90,
    rounding: 100,
    people: [p1, p2, p3],
    gas: { dist: '200', eff: '12', price: '175', manual: '', useManual: false, paidBy: p1.id },
    expenses: [{ id: uid(), name: '高速代', amount: '2400', paidBy: p1.id, targets: 'all', targetIds: [] }],
  };
}

// ── PersonCard ─────────────────────────────────────────────────
function PersonCard({ person, index, paidAmt, canRemove, onUpdate, onRemove }) {
  const inputId = `person-name-${person.id}`;
  return (
    <div className="fs-person-card">
      <div className="fs-person-card-top">
        <div className={`fs-person-avatar${person.isDriver ? ' fs-person-avatar--driver' : ''}`}>
          {initials(person.name)}
        </div>
        <div className="fs-person-name-wrap">
          <label htmlFor={inputId} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
            参加者{index + 1}の名前
          </label>
          <input
            id={inputId}
            type="text"
            autoComplete="name"
            className="fs-input fs-person-name-input"
            placeholder={`参加者 ${index + 1}`}
            value={person.name}
            onChange={e => onUpdate({ name: e.target.value })}
          />
        </div>
        {canRemove && (
          <button className="fs-remove-btn" onClick={onRemove} aria-label="削除">×</button>
        )}
      </div>

      <div className="fs-driver-toggle">
        <button
          className={person.isDriver ? 'active-driver' : ''}
          onClick={() => onUpdate({ isDriver: true })}
        >
          🚗 運転者
        </button>
        <button
          className={!person.isDriver ? 'active-pass' : ''}
          onClick={() => onUpdate({ isDriver: false })}
        >
          👤 同乗者
        </button>
      </div>

      {paidAmt > 0 && (
        <div className="fs-person-paid-info">
          <span className="fs-person-paid-lbl">立替済み</span>
          <span className="fs-person-paid-amt">¥{fmt(paidAmt)}</span>
        </div>
      )}
    </div>
  );
}

// ── GasSection ────────────────────────────────────────────────
function GasSection({ gas, people, onUpdate }) {
  const gasAmt = calcGas(gas);
  const missingPayer = gasAmt > 0 && !gas.paidBy;

  return (
    <div>
      <div className="fs-mode-switch">
        <button
          className={`fs-mode-switch-btn${!gas.useManual ? ' active' : ''}`}
          onClick={() => onUpdate({ useManual: false })}
        >
          ⚙️ 自動計算
        </button>
        <button
          className={`fs-mode-switch-btn${gas.useManual ? ' active' : ''}`}
          onClick={() => onUpdate({ useManual: true })}
        >
          ✏️ 直接入力
        </button>
      </div>
      <div className="fs-mode-hint">
        {!gas.useManual
          ? '走行距離からガソリン代を自動計算します（おすすめ）'
          : 'すでに金額が分かっている場合はここに入力'}
      </div>

      {!gas.useManual ? (
        <>
          <div className="fs-gas-grid">
            <div className="fs-field-row">
              <label htmlFor="gas-dist" className="fs-field-label">走行距離 km</label>
              <input
                id="gas-dist"
                type="number"
                className="fs-input fs-input--mono"
                placeholder="往復 例:200"
                min="0"
                value={gas.dist}
                onChange={e => onUpdate({ dist: e.target.value })}
              />
              <div className="fs-dist-hint">片道距離 × 2 の往復距離を入力してください</div>
            </div>
            <div className="fs-field-row">
              <label htmlFor="gas-eff" className="fs-field-label">燃費 km/L</label>
              <input
                id="gas-eff"
                type="number"
                className="fs-input fs-input--mono"
                placeholder="例:12"
                min="1"
                step="0.1"
                value={gas.eff}
                onChange={e => onUpdate({ eff: e.target.value })}
              />
            </div>
            <div className="fs-field-row">
              <label htmlFor="gas-price" className="fs-field-label">ガソリン単価 円/L</label>
              <input
                id="gas-price"
                type="number"
                className="fs-input fs-input--mono"
                placeholder="例:175"
                min="0"
                value={gas.price}
                onChange={e => onUpdate({ price: e.target.value })}
              />
            </div>
          </div>
          {!gasAmt && (parseFloat(gas.dist) || parseFloat(gas.eff) || parseFloat(gas.price)) && (
            <div className="fs-mode-hint">走行距離・燃費・単価をすべて入力すると自動計算します</div>
          )}
          {gasAmt > 0 && (
            <div className="fs-gas-result-bar">
              <div>
                <div className="grb-label">推定ガソリン代</div>
                <div className="grb-formula">{gas.dist}km ÷ {gas.eff}km/L × ¥{gas.price}/L</div>
              </div>
              <div className="grb-value">¥{fmt(gasAmt)}</div>
            </div>
          )}
        </>
      ) : (
        <div className="fs-field-row">
          <label htmlFor="gas-manual" className="fs-field-label">ガソリン代（円）</label>
          <input
            id="gas-manual"
            type="number"
            className="fs-input fs-input--mono"
            placeholder="例：3,500"
            min="0"
            value={gas.manual}
            onChange={e => onUpdate({ manual: e.target.value })}
          />
        </div>
      )}

      {gasAmt > 0 && (
        <div className="fs-field-row" style={{ marginBottom: 0 }}>
          <label
            htmlFor="gas-paidby"
            className="fs-field-label"
            style={missingPayer ? { color: 'var(--ember)' } : {}}
          >
            誰が払った？
          </label>
          <select
            id="gas-paidby"
            className="fs-select"
            value={gas.paidBy}
            style={missingPayer ? { borderColor: 'var(--ember)' } : {}}
            onChange={e => onUpdate({ paidBy: e.target.value })}
          >
            <option value="">— 払った人を選んでください —</option>
            {people.map(p => (
              <option key={p.id} value={p.id}>{p.name || '名前未設定'}</option>
            ))}
          </select>
          {missingPayer && (
            <div className="fs-field-error">ガソリン代を立替払いした人を選んでください</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ExpenseCard ────────────────────────────────────────────────
function ExpenseCard({ expense, people, canRemove, onUpdate, onRemove }) {
  const [amtFocused, setAmtFocused] = useState(false);
  const hasAmt = parseFloat(expense.amount) > 0;
  const missingPayer = hasAmt && !expense.paidBy;
  const missingTargets = hasAmt && expense.targets === 'custom' && expense.targetIds.length === 0;
  const nameId = `exp-name-${expense.id}`;
  const amtId = `exp-amt-${expense.id}`;
  const payerId = `exp-payer-${expense.id}`;

  const displayAmt = amtFocused || !expense.amount
    ? expense.amount
    : Number(expense.amount).toLocaleString('ja-JP');

  const targetCount = expense.targets === 'all' ? people.length : expense.targetIds.length;

  return (
    <div className={`fs-expense-card${missingPayer || missingTargets ? ' fs-expense-card--error' : ''}`}>
      <div className="fs-expense-card-top">
        <div className="fs-expense-name-wrap">
          <label htmlFor={nameId} className="fs-field-label">費用名</label>
          <input
            id={nameId}
            type="text"
            className="fs-input"
            placeholder="例：東名高速、山頂駐車場"
            value={expense.name}
            onChange={e => onUpdate({ name: e.target.value })}
          />
        </div>
        <div className="fs-expense-amount-wrap">
          <label htmlFor={amtId} className="fs-field-label">金額（円）</label>
          <input
            id={amtId}
            type="text"
            inputMode="decimal"
            className="fs-input fs-input--mono"
            placeholder="0"
            value={displayAmt}
            onFocus={() => setAmtFocused(true)}
            onBlur={() => setAmtFocused(false)}
            onChange={e => onUpdate({ amount: e.target.value.replace(/[,，]/g, '') })}
          />
        </div>
        {canRemove && (
          <button className="fs-expense-remove" onClick={onRemove} aria-label="削除">×</button>
        )}
      </div>

      <div className="fs-expense-grid">
        <div className="fs-field-row" style={{ marginBottom: 0 }}>
          <label
            htmlFor={payerId}
            className="fs-field-label"
            style={missingPayer ? { color: 'var(--ember)' } : {}}
          >
            誰が払った？
          </label>
          <select
            id={payerId}
            className="fs-select"
            value={expense.paidBy}
            style={missingPayer ? { borderColor: 'var(--ember)' } : {}}
            onChange={e => onUpdate({ paidBy: e.target.value })}
          >
            <option value="">— 選んでください —</option>
            {people.map(p => (
              <option key={p.id} value={p.id}>{p.name || '名前未設定'}</option>
            ))}
          </select>
          {missingPayer && (
            <div className="fs-field-error">立替払いした人を選んでください</div>
          )}
        </div>
        <div className="fs-field-row" style={{ marginBottom: 0 }}>
          <span className="fs-field-label">請求対象者</span>
          <div className="fs-target-tabs">
            <button
              className={`fs-target-tab${expense.targets === 'all' ? ' active' : ''}`}
              onClick={() => onUpdate({ targets: 'all', targetIds: [] })}
            >
              全員
            </button>
            <button
              className={`fs-target-tab${expense.targets === 'custom' ? ' active' : ''}`}
              onClick={() => onUpdate({ targets: 'custom', targetIds: people.map(p => p.id) })}
            >
              個別
            </button>
          </div>
        </div>
      </div>

      {hasAmt && targetCount > 0 && (
        <div className="fs-per-person-hint">
          1人あたり約 ¥{Math.ceil(parseFloat(expense.amount) / targetCount).toLocaleString('ja-JP')}
        </div>
      )}

      {expense.targets === 'custom' && (
        <>
          <div className="fs-person-checks">
            {people.map(p => {
              const checked = expense.targetIds.includes(p.id);
              const toggle = () => {
                const ids = checked
                  ? expense.targetIds.filter(id => id !== p.id)
                  : [...expense.targetIds, p.id];
                onUpdate({ targetIds: ids });
              };
              return (
                <div
                  key={p.id}
                  className={`fs-person-check${checked ? ' checked' : ''}`}
                  onClick={toggle}
                >
                  <div className="fs-person-check-dot" />
                  {p.name || '名前未設定'}
                </div>
              );
            })}
          </div>
          {missingTargets && (
            <div className="fs-field-error">対象者を1人以上選んでください</div>
          )}
        </>
      )}
    </div>
  );
}

// ── ResultCards ────────────────────────────────────────────────
function ResultCards({ result, state }) {
  const [lineCopied, setLineCopied] = useState(false);

  if (!result || result.totalCost <= 0) {
    return (
      <div className="fs-result-empty">
        <div style={{ fontSize: 28, marginBottom: 10 }}>🧮</div>
        <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, fontSize: 14 }}>
          費用を入力すると精算結果が出ます
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          参加者・ガソリン代・費用を入力してみてください
        </div>
      </div>
    );
  }

  const allSettled = result.transfers.length === 0;

  const copyForLine = () => {
    const trip = state.tripName || '交通費精算';
    const lines = [`【${trip}】`, ''];
    if (allSettled) {
      lines.push('✅ 全員の支払いが均等でした！');
    } else {
      lines.push('💰 精算のお願い');
      result.transfers.forEach(t =>
        lines.push(`　${t.from}さん → ${t.to}さんに ¥${fmt(t.amount)} 払ってください`)
      );
    }
    lines.push('', `📊 合計費用：¥${fmt(result.totalCost)}`, '', '（交通費ワリカンで計算）');
    navigator.clipboard.writeText(lines.join('\n'))
      .then(() => { setLineCopied(true); setTimeout(() => setLineCopied(false), 2500); })
      .catch(() => {});
  };

  return (
    <div>
      <div className="fs-answer-headline">精算結果</div>

      {allSettled ? (
        <div className="fs-settled-card">
          <div className="fs-settled-icon">🎉</div>
          <div className="fs-settled-title">精算不要！全員が均等です</div>
          <div className="fs-settled-sub">誰かに払う必要はありません</div>
        </div>
      ) : (
        <div>
          {result.transfers.map((t, i) => (
            <div key={i} className="fs-result-card">
              <div className="fs-rc-from">{t.from}さんが支払う</div>
              <div className="fs-rc-arrow-row">
                <div className="fs-rc-to">{t.to}さん</div>
                <div className="fs-rc-arrow">に送金</div>
              </div>
              <div className="fs-rc-amount"><span>¥</span>{fmt(t.amount)}</div>
              <div className="fs-rc-action">↑ これをLINEで送ってOK</div>
            </div>
          ))}
        </div>
      )}

      <button
        className={`fs-line-copy-btn${lineCopied ? ' fs-line-copy-btn--copied' : ''}`}
        onClick={copyForLine}
      >
        {lineCopied ? '✅ コピーしました！LINEに貼ってください' : '📤 LINEに送る文章をコピー'}
      </button>

      <div className="fs-breakdown-title">内訳</div>
      {result.balances.map(b => {
        const netLabel = b.balance > 0.5 ? '受け取る' : b.balance < -0.5 ? '支払う' : '±0';
        const netClass = b.balance > 0.5 ? '--positive' : b.balance < -0.5 ? '--negative' : '--neutral';
        return (
          <div key={b.id} className="fs-breakdown-item">
            <div className="fs-bi-head">
              <div className={`fs-bi-avatar${b.isDriver ? ' fs-bi-avatar--driver' : ''}`}>
                {initials(b.name)}
              </div>
              <div>
                <span className="fs-bi-name">{b.name || '名前未設定'}</span>
                {b.isDriver && <span className="fs-bi-tag">運転者</span>}
              </div>
            </div>
            <div className="fs-bi-rows">
              <div className="fs-bi-cell">
                <div className="fs-bi-cell-label">負担額</div>
                <div className="fs-bi-cell-value fs-bi-cell-value--neutral">¥{fmt(b.owed)}</div>
              </div>
              <div className="fs-bi-cell">
                <div className="fs-bi-cell-label">立替済み</div>
                <div className="fs-bi-cell-value fs-bi-cell-value--neutral">¥{fmt(b.paid)}</div>
              </div>
              <div className="fs-bi-cell">
                <div className="fs-bi-cell-label">{netLabel}</div>
                <div className={`fs-bi-cell-value fs-bi-cell-value${netClass}`}>
                  ¥{fmt(Math.abs(b.balance))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── FareSplitPage ─────────────────────────────────────────────
function FareSplitPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isShared = !!searchParams.get('s');

  const [state, setState] = useState(() => {
    const encoded = searchParams.get('s');
    if (encoded) {
      const decoded = decodeState(encoded);
      if (decoded) return { ...makeDefault(), ...decoded };
    }
    return makeDefault();
  });

  const [shareUrl, setShareUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  const set = (key, val) => setState(s => ({ ...s, [key]: val }));
  const setGas = patch => setState(s => ({ ...s, gas: { ...s.gas, ...patch } }));
  const updPerson = (id, patch) =>
    setState(s => ({ ...s, people: s.people.map(p => p.id === id ? { ...p, ...patch } : p) }));
  const updExpense = (id, patch) =>
    setState(s => ({ ...s, expenses: s.expenses.map(e => e.id === id ? { ...e, ...patch } : e) }));

  const result = useMemo(() => computeResult(state), [state]);
  const totalCost = result?.totalCost || 0;

  const paidMap = useMemo(() => {
    if (!result) return {};
    const m = {};
    result.balances.forEach(b => { m[b.id] = b.paid; });
    return m;
  }, [result]);

  const hasErrors = useMemo(() => {
    if (calcGas(state.gas) > 0 && !state.gas.paidBy) return true;
    return state.expenses.some(e => {
      const a = parseFloat(e.amount) || 0;
      return (a > 0 && !e.paidBy) || (a > 0 && e.targets === 'custom' && e.targetIds.length === 0);
    });
  }, [state]);

  // ステップバー計算
  const step1Done = state.people.length >= 2 && state.people.every(p => p.name.trim());
  const step2Done = totalCost > 0 && !hasErrors;

  const getStepNumClass = (step) => {
    if (step === 1) return step1Done ? 'fs-step-num--done' : 'fs-step-num--active';
    if (step === 2) return step2Done ? 'fs-step-num--done' : step1Done ? 'fs-step-num--active' : 'fs-step-num--todo';
    return step2Done ? 'fs-step-num--active' : 'fs-step-num--todo';
  };

  const getStepLabelClass = (step) => {
    if (step === 1) return step1Done ? 'fs-step-label--done' : 'fs-step-label--active';
    if (step === 2) return step2Done ? 'fs-step-label--done' : step1Done ? 'fs-step-label--active' : '';
    return step2Done ? 'fs-step-label--active' : '';
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 10, behavior: 'smooth' });
  };

  const handleShare = () => {
    const encoded = encodeState(state);
    const base = `${window.location.origin}${window.location.pathname}`;
    const url = `${base}#/fare-split?s=${encodeURIComponent(encoded)}`;
    setShareUrl(url);
    navigator.clipboard.writeText(url)
      .then(() => { setShareCopied(true); setTimeout(() => setShareCopied(false), 2500); })
      .catch(() => {});
  };

  const handleReset = () => {
    setState(makeDefault());
    navigate('/fare-split');
  };

  const demoTransfers = result?.transfers || [];
  const demoTotal = result?.totalCost || 0;

  return (
    <div className="fs-page">

      {/* ═══ HERO ═══ */}
      <div className="fs-hero">
        <div className="fs-hero-eyebrow">🚗 交通費ワリカン</div>
        <h1 className="fs-hero-h1">誰がいくら払うか、<br /><em>10秒で分かる</em></h1>
        <div className="fs-hero-sub">
          面倒な割り勘、もう終わり。<br />ガソリン代から高速代まで一発計算。
        </div>

        {/* ライブ結果プレビュー */}
        <div className="fs-hero-demo">
          <div className="fs-hero-demo-label">あなたの精算結果（リアルタイム）</div>
          {demoTransfers.length === 0 && demoTotal === 0 && (
            <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 13, padding: '6px 0' }}>
              費用を入力すると、ここに結果が表示されます
            </div>
          )}
          {demoTransfers.length === 0 && demoTotal > 0 && (
            <div style={{ color: '#7adb9a', fontSize: 14, fontWeight: 700, padding: '6px 0' }}>
              🎉 全員精算済み！誰にも支払いは不要です
            </div>
          )}
          {demoTransfers.map((t, i) => (
            <div key={i} className="fs-demo-transfer">
              <div className="fs-demo-from">{t.from}</div>
              <div className="fs-demo-arrow">→</div>
              <div className="fs-demo-to">{t.to}</div>
              <div className="fs-demo-amount">¥{fmt(t.amount)}</div>
            </div>
          ))}
          {demoTotal > 0 && (
            <div className="fs-demo-total">
              <span className="fs-demo-total-label">合計費用</span>
              <span className="fs-demo-total-value">¥{fmt(demoTotal)}</span>
            </div>
          )}
        </div>

        <div className="fs-hero-cta-strip">
          <button className="fs-btn-hero-cta" onClick={() => scrollTo('sec-people')}>
            ▶ 参加者の名前を確認する
          </button>
          <div className="fs-hero-cta-note">入力するたびに上の結果が変わります</div>
        </div>
      </div>

      {/* ステップバー */}
      <div className="fs-step-bar">
        <div className="fs-step-item">
          <div className={`fs-step-num ${getStepNumClass(1)}`}>
            {step1Done ? '✓' : '1'}
          </div>
          <span className={`fs-step-label ${getStepLabelClass(1)}`}>参加者</span>
        </div>
        <span className="fs-step-sep">›</span>
        <div className="fs-step-item">
          <div className={`fs-step-num ${getStepNumClass(2)}`}>
            {step2Done ? '✓' : '2'}
          </div>
          <span className={`fs-step-label ${getStepLabelClass(2)}`}>費用入力</span>
        </div>
        <span className="fs-step-sep">›</span>
        <div className="fs-step-item">
          <div className={`fs-step-num ${getStepNumClass(3)}`}>3</div>
          <span className={`fs-step-label ${getStepLabelClass(3)}`}>結果を共有</span>
        </div>
      </div>

      {/* 共有バナー */}
      {isShared && (
        <div className="fs-shared-banner">
          <div className="fs-shared-banner-label">共有された精算</div>
          <div className="fs-shared-banner-btns">
            <button onClick={handleReset}>自分用にコピーして編集</button>
          </div>
        </div>
      )}

      {/* ① 旅行名 */}
      <div className="fs-section">
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">① 旅行名</div>
            <div className="fs-sec-title">精算シートのタイトル</div>
            <div className="fs-sec-hint">LINEでコピーするときに使われます（任意）</div>
          </div>
        </div>
        <div className="fs-sec-body">
          <div className="fs-field-row" style={{ marginBottom: 0 }}>
            <label htmlFor="trip-name" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
              旅行名
            </label>
            <input
              id="trip-name"
              type="text"
              className="fs-input"
              placeholder="例：富士山登山（6/15）"
              value={state.tripName}
              onChange={e => set('tripName', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ② 参加者 */}
      <div id="sec-people" className="fs-section">
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">② 参加者</div>
            <div className="fs-sec-title">誰が乗るか入力する</div>
            <div className="fs-sec-hint">名前を変えてください。運転者も設定できます</div>
          </div>
          <div className="fs-sec-meta">{state.people.length}人</div>
        </div>
        <div className="fs-sec-body">
          {state.people.map((p, i) => (
            <PersonCard
              key={p.id}
              person={p}
              index={i}
              paidAmt={paidMap[p.id] || 0}
              canRemove={state.people.length > 2}
              onUpdate={patch => updPerson(p.id, patch)}
              onRemove={() => set('people', state.people.filter(x => x.id !== p.id))}
            />
          ))}
          <button
            className="fs-add-btn"
            onClick={() => set('people', [...state.people, { id: uid(), name: '', isDriver: false }])}
          >
            ＋ 参加者を追加
          </button>
        </div>
      </div>

      {/* ③ ガソリン代 */}
      <div className="fs-section">
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">③ ガソリン代</div>
            <div className="fs-sec-title">車の燃料費を計算する</div>
          </div>
          {calcGas(state.gas) > 0 && (
            <div className="fs-sec-meta">¥{fmt(calcGas(state.gas))}</div>
          )}
        </div>
        <div className="fs-sec-body">
          <GasSection gas={state.gas} people={state.people} onUpdate={setGas} />
        </div>
      </div>

      {/* ④ その他費用 */}
      <div className="fs-section">
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">④ その他費用</div>
            <div className="fs-sec-title">高速・駐車場などを追加する</div>
          </div>
          {state.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0) > 0 && (
            <div className="fs-sec-meta">
              ¥{fmt(state.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0))}
            </div>
          )}
        </div>
        <div className="fs-sec-body">
          {state.expenses.map(e => (
            <ExpenseCard
              key={e.id}
              expense={e}
              people={state.people}
              canRemove={state.expenses.length > 1}
              onUpdate={patch => updExpense(e.id, patch)}
              onRemove={() => set('expenses', state.expenses.filter(x => x.id !== e.id))}
            />
          ))}
          <div className="fs-highway-hint">
            高速料金が不明な場合 →{' '}
            <a
              href="https://www.driveplaza.com/dp/SearchTop"
              className="fs-highway-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              ドラぷら で検索
            </a>
          </div>
          <div className="fs-template-row">
            <div className="fs-template-row-label">ワンタップで追加</div>
            <div className="fs-template-btns">
              {['高速代', '駐車場代', 'レンタカー代', 'ガソリン代', 'コンビニ', '温泉代', '食事代'].map(name => (
                <button
                  key={name}
                  className="fs-template-btn"
                  onClick={() => set('expenses', [...state.expenses, newExpense(name)])}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
          <button
            className="fs-add-btn"
            onClick={() => set('expenses', [...state.expenses, newExpense()])}
          >
            ＋ 費用を追加
          </button>
          {totalCost > 0 && (
            <div className="fs-total-line">
              <span className="tl-label">合計費用</span>
              <span className="tl-value">¥{fmt(totalCost)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ⑤ 運転者の負担 */}
      <div className="fs-section">
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">⑤ 運転者の負担</div>
            <div className="fs-sec-title">運転者を割引する</div>
            <div className="fs-sec-hint">運転の手間を考慮して調整できます</div>
          </div>
          <div className="fs-sec-meta">
            {state.driverRate === 0 ? '無料' : state.driverRate === 100 ? '均等' : `${state.driverRate}%`}
          </div>
        </div>
        <div className="fs-sec-body">
          {/* ワンタップパターン */}
          <div className="fs-pattern-strip">
            <div className="fs-pattern-strip-label">⚡ よくあるパターン</div>
            <div className="fs-pattern-chips">
              {[
                { label: '👥 全員で均等割り', rate: 100 },
                { label: '🚗 運転者は少し安く', rate: 80 },
                { label: '✂️ 運転者は半額', rate: 50 },
                { label: '🎁 運転者は無料', rate: 0 },
              ].map(({ label, rate }) => (
                <button
                  key={rate}
                  className={`fs-pattern-chip${state.driverRate === rate ? ' active' : ''}`}
                  onClick={() => set('driverRate', rate)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="fs-driver-rate-row">
            <div className="fs-driver-rate-label">運転者の負担率</div>
            <div className="fs-driver-rate-value">
              {state.driverRate}<span>%</span>
            </div>
          </div>
          <input
            type="range"
            id="driver-rate"
            className="fs-range"
            min="0"
            max="100"
            step="5"
            value={state.driverRate}
            style={{ '--pct': `${state.driverRate}%` }}
            onChange={e => set('driverRate', parseInt(e.target.value))}
          />
          <div className="fs-slider-labels">
            <span>0% 無料</span>
            <span>50% 半額</span>
            <span>100% 均等</span>
          </div>

          {result && result.totalCost > 0 && state.people.some(p => p.isDriver) && (
            <div className="fs-driver-summary">
              <span>
                🚗 運転者{' '}
                <strong style={{ fontFamily: 'var(--fs-mono)', color: 'var(--gold)' }}>
                  {state.driverRate === 0 ? '無料' : state.driverRate === 100 ? '均等' : `${state.driverRate}%負担`}
                </strong>
              </span>
              {state.people.filter(p => p.isDriver).map(p => {
                const b = result.balances.find(b => b.id === p.id);
                return b ? (
                  <span key={p.id} style={{ fontFamily: 'var(--fs-mono)' }}>
                    {p.name}: ¥{fmt(b.owed)}
                  </span>
                ) : null;
              })}
              {state.people.some(p => !p.isDriver) && (
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  👤 同乗者1人 ¥{fmt(
                    result.balances.filter(b => !b.isDriver).reduce((s, b) => s + b.owed, 0) /
                    Math.max(1, result.balances.filter(b => !b.isDriver).length)
                  )}
                </span>
              )}
            </div>
          )}

          <div className="fs-rounding-row">
            <label className="fs-field-label" style={{ marginBottom: 8 }}>端数処理</label>
            <div className="fs-round-btns">
              {[1, 10, 100].map(u => (
                <button
                  key={u}
                  className={`fs-round-btn${state.rounding === u ? ' active' : ''}`}
                  onClick={() => set('rounding', u)}
                >
                  {u === 1 ? '1円' : u === 10 ? '10円' : '100円'}単位
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ⑥ 精算結果 */}
      <div id="sec-result" className="fs-section">
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">⑥ 精算結果</div>
            <div className="fs-sec-title">誰がいくら払うか</div>
          </div>
        </div>
        <div className="fs-sec-body">
          <ResultCards result={result} state={state} />
        </div>
      </div>

      {/* ⑦ 共有 */}
      <div className="fs-section" style={{ borderBottom: 'none' }}>
        <div className="fs-sec-head">
          <div>
            <div className="fs-sec-eyebrow">⑦ 共有</div>
            <div className="fs-sec-title">URLで共有する</div>
            <div className="fs-sec-hint">リンクを知っていれば誰でも見られます</div>
          </div>
        </div>
        <div className="fs-sec-body">
          <button className="fs-share-btn" onClick={handleShare}>
            <span>🔗</span><span>共有URLをコピー</span>
          </button>
          {shareUrl && (
            <div className="fs-share-url-box">
              {shareUrl}
              {shareCopied && (
                <div><span className="fs-copy-ok">✓ コピーしました</span></div>
              )}
            </div>
          )}
          <div className="fs-share-notice">
            <strong>⚠ プライバシーにご注意ください</strong>
            共有URLを知っている人は入力内容を閲覧できます。個人情報は入力しないでください。
          </div>
        </div>

        <Link to="/recruitments" className="fs-footer-link">
          ヤマッチで登山メンバーを探す →
        </Link>
      </div>

      {/* BOTTOM BAR（常時表示） */}
      <div className="fs-bottom-bar">
        <div className="fs-bb-total">
          <span className="fs-bb-label">合計費用</span>
          <span className="fs-bb-amount">
            {totalCost > 0 ? `¥${fmt(totalCost)}` : '入力待ち'}
          </span>
        </div>
        <button
          className={`fs-bb-btn${totalCost === 0 ? ' fs-bb-btn--idle' : hasErrors ? ' fs-bb-btn--warn' : ''}`}
          onClick={() => totalCost > 0 && scrollTo('sec-result')}
        >
          {totalCost === 0
            ? '費用を入力する'
            : hasErrors
            ? '⚠ 不備あり — 確認する'
            : '誰がいくら払うか確認する →'}
        </button>
      </div>
    </div>
  );
}

export default FareSplitPage;
